import React from 'react'
import { injectIntl } from 'react-intl'
import { compose, withProps, lifecycle } from 'recompose'
import { SearchBox } from 'react-google-maps/lib/components/places/SearchBox'
import isEmpty from 'lodash.isempty'
import get from 'lodash.get'

import { withScriptjs, withGoogleMap, GoogleMap, Marker } from 'react-google-maps'
import {
  parseGoogleResponse,
  logMessage,
  getApiSrcImagesUrl,
  handleKeyDown,
} from 'src/utils/helpers'
import cx from 'classnames'

import Spin from 'src/components/Spin'
import Button from 'src/components/Button/Button'

import situerStyle from '../styles'
import styles from './styles.module.less'
import './styles.less'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon/index'
import intlMessages from 'src/components/Maps/translations'

const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY || ''

export default compose(
  injectIntl,
  withProps({
    googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&v=3.exp&libraries=geometry,places`,
    loadingElement: <div className={styles.googleMapLoading} />,
    containerElement: <div className={styles.googleMapContainer} />,
    mapElement: <div className={styles.googleMap} />,
  }),
  lifecycle({
    componentDidMount() {
      const refs = {}
      this.setState({
        bounds: null,
        center: {
          lat: this.props.lat || '',
          lng: this.props.lng || '',
        },
        markers: [
          {
            position: {
              lat: this.props.lat || '',
              lng: this.props.lng || '',
            },
          },
        ],
        isSearchLoading: false,
        onMapMounted: ref => {
          refs.map = ref
        },
        onSearchBoxMounted: ref => {
          refs.searchBox = ref
        },
        onSearchFieldMounted: ref => {
          refs.searchField = ref
          if (this.props.isWidget && ref) {
            ref.addEventListener('keypress', () => {
              const searchHolder = ref.parentNode
              const pac =
                document.querySelector('.pac-container') ||
                searchHolder.querySelector('.pac-container')
              if (!searchHolder.contains(pac)) {
                pac.classList.add('pac-container-widget-position')
                searchHolder.appendChild(pac)
              }
            })
          }
        },
        onPlacesChanged: () => {
          this.setState({
            isSearchLoading: true,
          })

          const places = refs.searchBox.getPlaces()
          const bounds = new google.maps.LatLngBounds() //eslint-disable-line
          const address = get(places, '0.address_components', {})
          const position = get(places, '0.geometry.location', this.state.center)

          if (isEmpty(address)) {
            return
          }
          places.forEach(place => {
            if (place.geometry.viewport) {
              bounds.union(place.geometry.viewport)
            } else {
              bounds.extend(place.geometry.location)
            }
          })
          const nextMarkers = places.map(place => ({
            position: place.geometry.location,
          }))

          this.setState({
            isSearchLoading: false,
            center: position,
            markers: nextMarkers,
          })
          refs.map.fitBounds(bounds)

          this.props.onPlaceChange({
            ...parseGoogleResponse(address),
            lat: position.lat(),
            lng: position.lng(),
          })
        },
        searchIconClick: (event, shouldClear = false) => {
          try {
            event.preventDefault()

            if (!refs.searchField) {
              return false
            }

            if (shouldClear) {
              refs.searchField.value = ''
            }

            return true
          } catch (e) {
            return false
          }
        },
      })
    },
  }),
  withScriptjs,
  withGoogleMap,
)(props => (
  <GoogleMap
    ref={props.onMapMounted}
    defaultZoom={props.defaultZoom || 20}
    center={props.center}
    options={{
      disableDefaultUI: true,
      scrollwheel: false,
      zoomControl: window.innerWidth < 800 ? false : true,
      styles: situerStyle,
    }}
  >
    <div id='mapLocationContainer' />
    <div style={{ position: 'fixed' }}>
      <SearchBox
        ref={props.onSearchBoxMounted}
        bounds={props.bounds}
        controlPosition={google.maps.ControlPosition.TOP_LEFT} //eslint-disable-line
        getPlaces={places => logMessage('', { places })}
        onPlacesChanged={props.onPlacesChanged}
      >
        <div className={styles.findBarHolder}>
          <div className={styles.findBarHolderInner}>
            {props.isSearchLoading ? (
              <div className={styles.loadingSearchIconHolder}>
                <Spin size='small' />
              </div>
            ) : (
              <div className={styles.searchIconHolder} onClick={props.searchIconClick}>
                <FontAwesomeIconComponent
                  icon={['far', 'magnifying-glass']}
                  size={'xs'}
                  type='input-icon-color'
                />
              </div>
            )}
            <div className={styles.searchHolder}>
              <input
                ref={props.onSearchFieldMounted}
                name='mapFindLocation'
                className={cx({
                  [styles.searchField]: true,
                  [styles.searchFieldAlt]: true,
                })}
                placeholder={props.intl.formatMessage(intlMessages.mapFindLocationFieldPlaceholder)}
                type='text'
              />
            </div>
            <div
              className={cx({
                [styles.clearSearchIconHolder]: true,
                [styles.clearSearchIconHolderAlt]: true,
              })}
              onClick={e => props.searchIconClick(e, true)}
              onKeyDown={handleKeyDown(e => props.searchIconClick(e, true))}
              tabIndex={0}
            >
              <FontAwesomeIconComponent
                icon={['far', 'xmark']}
                size={'xs'}
                type='input-icon-color'
              />
            </div>
            <div>
              <div
                className={styles.findMyLocation}
                onClick={props.onFindMe}
                onKeyDown={handleKeyDown(props.onFindMe)}
                tabIndex={0}
              >
                {props.isFindMeLoading ? (
                  <div className={styles.loadingFindMeIconHolder}>
                    <Spin size='small' />
                  </div>
                ) : (
                  <div className={styles.findMyLocationIconHolder}>
                    <FontAwesomeIconComponent
                      addClassName={styles.locationIcon}
                      icon={['fal', 'location-crosshairs']}
                      size={'sm'}
                      type={props.isFindMe ? 'accent' : 'input-icon-color'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SearchBox>
    </div>
    {props.markers.map((marker, index) => (
      <Marker
        key={index}
        position={marker.position}
        icon={
          new google.maps.MarkerImage( //eslint-disable-line
            `${getApiSrcImagesUrl()}/map/location-pin-plain-regular.svg`,
            null /* size is determined at runtime */,
            null /* origin is 0,0 */,
            null /* anchor is bottom center of the scaled image */,
            new google.maps.Size(34, 46), //eslint-disable-line
          )
        }
      />
    ))}
    <div
      // eslint-disable-next-line react/no-unknown-property
      display-if={props.isOnline}
      className={styles.submitButton}
    >
      <Button width={'fit-content'} onClick={props.onConfirm}>
        {props.confirmLocationButton}
      </Button>
    </div>
  </GoogleMap>
))
